import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { Plus, Search, X, Pencil, Trash2, ChevronDown, Users, Rocket } from 'lucide-react';
import { EmptyState } from '../components/ui';

const STATI = ['prospect','attivo','inattivo','chiuso'];
const STATO_STYLE = {
  prospect:  { background: 'var(--yellow-dim)', color: 'var(--yellow)' },
  attivo:    { background: 'var(--green-dim)',  color: 'var(--green)'  },
  inattivo:  { background: 'var(--surface)',    color: 'var(--text-muted)' },
  chiuso:    { background: 'var(--red-dim)',    color: 'var(--red)'    },
};

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24,
    }}>
      <div className="eden-card fade-up" style={{
        width: '100%', maxWidth: 520,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 6 }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '20px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 5, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ClienteForm({ initial, onSave, onClose }) {
  const empty = { nome:'', cognome:'', azienda:'', email:'', telefono:'', indirizzo:'', note:'', stato:'prospect' };
  const [form, setForm] = useState(initial ? { ...empty, ...initial } : empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async e => {
    e.preventDefault(); setLoading(true);
    try {
      if (initial?.id) await api.put(`/clienti/${initial.id}`, form);
      else await api.post('/clienti', form);
      onSave();
    } catch { alert('Errore nel salvataggio'); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="form-grid-2">
        <Field label="Nome *">
          <input className="eden-input" value={form.nome} onChange={e => set('nome', e.target.value)} required placeholder="Mario" />
        </Field>
        <Field label="Cognome">
          <input className="eden-input" value={form.cognome} onChange={e => set('cognome', e.target.value)} placeholder="Rossi" />
        </Field>
        <Field label="Azienda">
          <input className="eden-input" value={form.azienda} onChange={e => set('azienda', e.target.value)} placeholder="Acme Srl" />
        </Field>
        <Field label="Email">
          <input className="eden-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="mario@example.com" />
        </Field>
        <Field label="Telefono">
          <input className="eden-input" value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="+39 333 000 0000" />
        </Field>
        <Field label="Stato">
          <select className="eden-select" value={form.stato} onChange={e => set('stato', e.target.value)} style={{ width: '100%' }}>
            {STATI.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Indirizzo">
        <input className="eden-input" value={form.indirizzo} onChange={e => set('indirizzo', e.target.value)} placeholder="Via Roma 1, Milano" />
      </Field>
      <Field label="Note">
        <textarea className="eden-input" value={form.note} onChange={e => set('note', e.target.value)} rows={3} style={{ resize: 'vertical' }} placeholder="Note interne..." />
      </Field>
      <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
        <button type="submit" disabled={loading} className="eden-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
          {loading ? 'Salvataggio...' : 'Salva'}
        </button>
        <button type="button" onClick={onClose} className="eden-btn-ghost">Annulla</button>
      </div>
    </form>
  );
}

export default function Clienti() {
  const [clienti, setClienti] = useState([]);
  const [search, setSearch] = useState('');
  const [statoFilter, setStatoFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editCliente, setEditCliente] = useState(null);
  const [detailCliente, setDetailCliente] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    const res = await api.get('/clienti?limit=200');
    setClienti(res.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-apri cliente se ?open=<id> è nell'URL (da notifica o log)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || !clienti.length) return;
    const cliente = clienti.find(c => c.id === openId);
    if (cliente) {
      setDetailCliente(cliente);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, clienti, setSearchParams]);

  // Apri il form se ?new=1 (da Quick-add globale)
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setEditCliente(null); setModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filtered = clienti.filter(c => {
    const q = search.toLowerCase();
    return (!q || [c.nome, c.cognome, c.azienda, c.email].join(' ').toLowerCase().includes(q))
        && (!statoFilter || c.stato === statoFilter);
  });

  const handleDelete = async id => {
    if (!window.confirm('Eliminare questo cliente?')) return;
    await api.delete(`/clienti/${id}`);
    load();
  };

  const TH = ({ children }) => (
    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {children}
    </th>
  );

  return (
    <div className="eden-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap:'wrap', gap:12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Clienti</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="eden-btn" onClick={() => { setEditCliente(null); setModalOpen(true); }} title="Solo anagrafica">
            <Plus size={15} /> Aggiunta rapida
          </button>
          <button className="eden-btn is-primary" onClick={() => navigate('/onboarding')} title="Cliente, brand, accessi, team, checklist e primo PED">
            <Rocket size={15} /> Nuovo cliente
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="eden-card" style={{ padding: '14px 16px', marginBottom: 16, display: 'flex', flexDirection:'column', gap: 12 }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} color="var(--text-dim)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            className="eden-input"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome, azienda, email..."
            style={{ paddingLeft: 36 }}
          />
        </div>
        <div className="eden-scroll-row" style={{ gap:7 }}>
          <button onClick={() => setStatoFilter('')}
            style={{ fontSize:11.5, fontWeight:600, padding:'5px 11px', borderRadius:999, cursor:'pointer', touchAction:'manipulation',
              border:`1px solid ${!statoFilter ? 'var(--cyan)' : 'var(--border)'}`,
              background: !statoFilter ? 'var(--cyan-dim)' : 'var(--surface)',
              color: !statoFilter ? 'var(--cyan)' : 'var(--text-muted)' }}>
            Tutti <span style={{ opacity:0.7 }}>{clienti.length}</span>
          </button>
          {STATI.map(s => {
            const st = STATO_STYLE[s] || {}; const active = statoFilter === s;
            const n = clienti.filter(c => c.stato === s).length;
            return (
              <button key={s} onClick={() => setStatoFilter(active ? '' : s)}
                style={{ fontSize:11.5, fontWeight:600, padding:'5px 11px', borderRadius:999, cursor:'pointer', textTransform:'capitalize', touchAction:'manipulation',
                  border:`1px solid ${active ? (st.color || 'var(--cyan)') : 'var(--border)'}`,
                  ...(active ? st : { background:'var(--surface)', color:'var(--text-muted)' }) }}>
                {s} <span style={{ opacity:0.7 }}>{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="eden-card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="eden-table-stack" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <TH>Azienda / Nome</TH><TH>Email</TH><TH>Telefono</TH><TH>Stato</TH><TH>Azioni</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    <EmptyState icon={Users}
                      title={search || statoFilter ? 'Nessun cliente per questi filtri' : 'Nessun cliente'}
                      hint={search || statoFilter ? 'Prova a rimuovere i filtri o cambiare ricerca.' : 'Aggiungi il primo cliente con il pulsante “Nuovo cliente”.'} />
                  </td>
                </tr>
              ) : filtered.map((c, i) => (
                <tr
                  key={c.id}
                  style={{
                    borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '13px 16px', cursor: 'pointer' }} onClick={() => setDetailCliente(c)}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:30, height:30, borderRadius:'50%', flexShrink:0, overflow:'hidden', background:'var(--surface)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'var(--text-muted)' }}>
                        {c.logo_url
                          ? <img src={c.logo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                          : (c.azienda || c.nome || '—').slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{c.azienda || '—'}</div>
                        {(c.nome || c.cognome) && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{c.nome} {c.cognome}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="td-meta" data-vuoto={c.email ? undefined : ''} style={{ padding: '13px 16px', fontSize: 13, color: 'var(--text-muted)' }}>{c.email || '—'}</td>
                  <td className="td-meta" data-vuoto={c.telefono ? undefined : ''} style={{ padding: '13px 16px', fontSize: 13, color: 'var(--text-muted)' }}>{c.telefono || '—'}</td>
                  <td className="td-push" style={{ padding: '13px 16px' }}>
                    <span className="eden-badge" style={STATO_STYLE[c.stato] || STATO_STYLE.inattivo}>{c.stato}</span>
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setEditCliente(c); setModalOpen(true); }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 5, borderRadius: 6, display: 'flex', transition: 'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--cyan)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dim)'}
                        title="Modifica"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(c.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 5, borderRadius: 6, display: 'flex', transition: 'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dim)'}
                        title="Elimina"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-dim)' }}>
            {filtered.length} {filtered.length === 1 ? 'cliente' : 'clienti'}
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal title={editCliente ? 'Modifica cliente' : 'Nuovo cliente'} onClose={() => setModalOpen(false)}>
          <ClienteForm initial={editCliente} onSave={() => { load(); setModalOpen(false); }} onClose={() => setModalOpen(false)} />
        </Modal>
      )}

      {detailCliente && (
        <Modal title={`${detailCliente.nome} ${detailCliente.cognome || ''}`} onClose={() => setDetailCliente(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              ['Azienda', detailCliente.azienda],
              ['Email', detailCliente.email],
              ['Telefono', detailCliente.telefono],
              ['Indirizzo', detailCliente.indirizzo],
            ].filter(([,v]) => v).map(([l, v]) => (
              <div key={l}>
                <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</span>
                <p style={{ marginTop: 4, fontSize: 13.5, color: 'var(--text)' }}>{v}</p>
              </div>
            ))}
            <div>
              <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stato</span>
              <div style={{ marginTop: 6 }}>
                <span className="eden-badge" style={STATO_STYLE[detailCliente.stato] || STATO_STYLE.inattivo}>{detailCliente.stato}</span>
              </div>
            </div>
            {detailCliente.note && (
              <div>
                <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Note</span>
                <p style={{ marginTop: 6, fontSize: 13.5, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', lineHeight: 1.6 }}>
                  {detailCliente.note}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
